import React from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  Text,
  TextStyle,
  View,
  ViewStyle,
} from 'react-native';

import {
  PersistedStepUomActionRow,
  useIsCompactUomActionBar,
} from './PersistedStepUomActionRow';
import { colors, fontSize, fontWeight, lineHeight, spacing } from '../theme';

export type PersistedStepHeaderProps = {
  title: string;
  uomSlot: React.ReactNode;
  actionsSlot: React.ReactNode;
  /** Renders before the step H1 (e.g. web drill-in back control). */
  titleLeading?: React.ReactNode;
  titleTestID?: string;
  titleStyle?: StyleProp<TextStyle>;
  titleLeadingRowStyle?: StyleProp<ViewStyle>;
  compactMaxWidth?: number;
  testID?: string;
};

/**
 * Request Detail step chrome: step H1 + UOM/actions row.
 * Compact (mobile): title + Basic/Advanced on one row, UOM below.
 * Wide: UOM row above title (legacy desktop order).
 */
export const PersistedStepHeader: React.FC<PersistedStepHeaderProps> = ({
  title,
  uomSlot,
  actionsSlot,
  titleLeading,
  titleTestID,
  titleStyle,
  titleLeadingRowStyle,
  compactMaxWidth,
  testID,
}) => {
  const isCompact = useIsCompactUomActionBar(compactMaxWidth);

  if (isCompact) {
    return (
      <View style={styles.compactShell}>
        <View
          style={[
            styles.titleActionsRow,
            titleLeading != null && styles.titleActionsRowWithLeading,
            titleLeadingRowStyle,
          ]}
          testID="persisted-step-title-actions-row"
        >
          {titleLeading}
          <Text
            style={[
              styles.pageTitle,
              styles.pageTitleInTitleActionsRow,
              titleStyle,
            ]}
            accessibilityRole="header"
            testID={titleTestID}
          >
            {title}
          </Text>
          {actionsSlot != null ? (
            <View style={styles.titleActionsEnd}>{actionsSlot}</View>
          ) : null}
        </View>
        {uomSlot != null ? (
          <View style={styles.uomOnlyRow} testID={testID}>
            {uomSlot}
          </View>
        ) : null}
      </View>
    );
  }

  const titleBlock = (
    <View
      style={[
        styles.titleRow,
        titleLeading != null && styles.titleRowWithLeading,
        titleLeadingRowStyle,
      ]}
    >
      {titleLeading}
      <Text
        style={[styles.pageTitle, titleStyle]}
        accessibilityRole="header"
        testID={titleTestID}
      >
        {title}
      </Text>
    </View>
  );

  return (
    <>
      <PersistedStepUomActionRow
        uomSlot={uomSlot}
        actionsSlot={actionsSlot}
        compactMaxWidth={compactMaxWidth}
        testID={testID}
      />
      {titleBlock}
    </>
  );
};

PersistedStepHeader.displayName = 'PersistedStepHeader';

const styles = StyleSheet.create({
  compactShell: {
    width: '100%',
  },
  titleActionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.fieldColumnGapWeb,
    width: '100%',
    marginBottom: spacing.belowUomRow,
  },
  titleActionsRowWithLeading: {
    flexWrap: 'nowrap',
  },
  titleActionsEnd: {
    flexShrink: 0,
    flexGrow: 0,
  },
  uomOnlyRow: {
    width: '100%',
    marginBottom: spacing.belowUomRow,
  },
  titleRow: {
    marginBottom: spacing.belowFormStepPageTitle,
  },
  titleRowWithLeading: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  pageTitle: {
    fontSize: fontSize.formStepPageTitle,
    lineHeight: lineHeight.formStepPageTitle,
    fontWeight: fontWeight.bold,
    color: colors.primary,
    ...Platform.select({
      web: {
        marginBottom: 0,
      },
      default: {},
    }),
  },
  pageTitleInTitleActionsRow: {
    flex: 1,
    minWidth: 0,
    marginBottom: 0,
  },
});
